import React from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { ArrowDown, ArrowUp, ArrowUpDown, Eye, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  SyncEntityType,
  SyncJob,
  SyncJobStatus,
  SyncJobsFilters,
  SyncJobsSortField,
} from "@/types/pms";
import {
  formatDateTime,
  formatDateTimeUtc,
  formatDuration,
  formatRelativeTime,
} from "@/utils/dateUtils";
import { cn } from "@/lib/utils";
import { DEFAULT_SYNC_JOBS_FILTERS } from "../utils/syncJobsFilters";
import {
  syncJobStatusClass,
  syncJobStatusPulses,
  syncJobTypeClass,
} from "../utils/syncJobVariants";

const ERROR_PREVIEW_LIMIT = 80;

const entityShortKeys: Record<SyncEntityType, string> = {
  [SyncEntityType.HOTEL]: "pms.sync.entityShort.HOTEL",
  [SyncEntityType.ROOM_TYPE]: "pms.sync.entityShort.ROOM_TYPE",
  [SyncEntityType.ROOM]: "pms.sync.entityShort.ROOM",
  [SyncEntityType.GUEST]: "pms.sync.entityShort.GUEST",
  [SyncEntityType.RESERVATION]: "pms.sync.entityShort.RESERVATION",
  [SyncEntityType.PRODUCT]: "pms.sync.entityShort.PRODUCT",
  [SyncEntityType.SERVICE_CATEGORY]: "pms.sync.entityShort.SERVICE_CATEGORY",
  [SyncEntityType.SERVICE]: "pms.sync.entityShort.SERVICE",
  [SyncEntityType.RATE_PLAN]: "pms.sync.entityShort.RATE_PLAN",
  [SyncEntityType.RATE]: "pms.sync.entityShort.RATE",
  [SyncEntityType.FOLIO]: "pms.sync.entityShort.FOLIO",
};

interface SyncJobsTableProps {
  jobs: SyncJob[];
  filters: SyncJobsFilters;
  hasActiveFilters: boolean;
  onFiltersChange: (next: SyncJobsFilters) => void;
  onView: (job: SyncJob) => void;
  onCancel: (jobId: string) => void;
  onResetFilters: () => void;
  isCancelling: boolean;
}

const summarizeProgress = (job: SyncJob, t: TFunction): string => {
  if (!Array.isArray(job.progress) || job.progress.length === 0) return "—";

  const parts = job.progress
    .filter((p) => p.processed > 0)
    .map((p) => {
      const success = p.created + p.updated;
      const label = t(entityShortKeys[p.entityType]);
      return `${success}/${p.processed} ${label}`;
    });

  return parts.length > 0 ? parts.join(", ") : "—";
};

interface SortableHeaderProps {
  field: SyncJobsSortField;
  label: string;
  filters: SyncJobsFilters;
  onChange: (next: SyncJobsFilters) => void;
}

const SortableHeader: React.FC<SortableHeaderProps> = ({
  field,
  label,
  filters,
  onChange,
}) => {
  const activeField = filters.sortBy ?? DEFAULT_SYNC_JOBS_FILTERS.sortBy;
  const activeDir =
    filters.sortDirection ?? DEFAULT_SYNC_JOBS_FILTERS.sortDirection;
  const isActive = activeField === field;

  let Icon = ArrowUpDown;
  if (isActive) {
    Icon = activeDir === "asc" ? ArrowUp : ArrowDown;
  }

  const handleClick = () => {
    if (!isActive) {
      onChange({ ...filters, sortBy: field, sortDirection: "desc" });
      return;
    }
    onChange({
      ...filters,
      sortBy: field,
      sortDirection: activeDir === "desc" ? "asc" : "desc",
    });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        "inline-flex items-center gap-1.5 text-left font-medium hover:text-foreground",
        isActive ? "text-foreground" : "text-muted-foreground",
      )}
    >
      {label}
      <Icon className="size-3.5" />
    </button>
  );
};

const ErrorCell: React.FC<{ error?: string }> = ({ error }) => {
  if (!error) return <>—</>;
  const truncated =
    error.length > ERROR_PREVIEW_LIMIT
      ? `${error.slice(0, ERROR_PREVIEW_LIMIT)}…`
      : error;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="block max-w-[220px] truncate text-sm text-muted-foreground">
          {truncated}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-md whitespace-pre-wrap break-words">
        {error}
      </TooltipContent>
    </Tooltip>
  );
};

export const SyncJobsTable = ({
  jobs,
  filters,
  hasActiveFilters,
  onFiltersChange,
  onView,
  onCancel,
  onResetFilters,
  isCancelling,
}: SyncJobsTableProps) => {
  const { t } = useTranslation();

  if (jobs.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-8 text-center">
        <p className="text-muted-foreground">
          {hasActiveFilters
            ? t("pms.sync.noJobsMatchingFilters")
            : t("pms.sync.noJobsYet")}
        </p>
        {hasActiveFilters && (
          <Button variant="new_outline" size="sm" onClick={onResetFilters}>
            {t("pms.sync.resetFilters")}
          </Button>
        )}
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              <SortableHeader
                field="createdAt"
                label={t("pms.sync.columns.created")}
                filters={filters}
                onChange={onFiltersChange}
              />
            </TableHead>
            <TableHead>{t("pms.sync.columns.type")}</TableHead>
            <TableHead>
              <SortableHeader
                field="status"
                label={t("pms.sync.columns.status")}
                filters={filters}
                onChange={onFiltersChange}
              />
            </TableHead>
            <TableHead>{t("pms.sync.columns.started")}</TableHead>
            <TableHead>{t("pms.sync.columns.duration")}</TableHead>
            <TableHead>{t("pms.sync.columns.entities")}</TableHead>
            <TableHead>{t("pms.sync.columns.failureReason")}</TableHead>
            <TableHead className="text-right">
              {t("pms.sync.columns.actions")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {jobs.map((job) => {
            const canCancel =
              job.status === SyncJobStatus.PENDING ||
              job.status === SyncJobStatus.RUNNING;

            return (
              <TableRow key={job.id}>
                <TableCell>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-sm text-muted-foreground">
                        {formatDateTime(job.createdAt)}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {formatDateTimeUtc(job.createdAt)}
                    </TooltipContent>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Badge className={cn("border-0", syncJobTypeClass[job.type])}>
                    {job.type}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge
                    className={cn("border-0", syncJobStatusClass[job.status])}
                  >
                    {syncJobStatusPulses[job.status] && (
                      <span className="mr-1.5 inline-block size-1.5 animate-pulse rounded-full bg-current" />
                    )}
                    {job.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {job.startedAt ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>{formatRelativeTime(job.startedAt)}</span>
                      </TooltipTrigger>
                      <TooltipContent>
                        {formatDateTimeUtc(job.startedAt)}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {formatDuration(job.startedAt, job.completedAt) || "—"}
                </TableCell>
                <TableCell className="text-sm">
                  {summarizeProgress(job, t)}
                </TableCell>
                <TableCell>
                  <ErrorCell error={job.error} />
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="new_ghost"
                      size="sm"
                      onClick={() => onView(job)}
                    >
                      <Eye className="mr-1 h-4 w-4" />
                      {t("pms.sync.view")}
                    </Button>
                    {canCancel && (
                      <Button
                        variant="new_ghost"
                        size="sm"
                        disabled={isCancelling}
                        onClick={() => onCancel(job.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <XCircle className="mr-1 h-4 w-4" />
                        {t("pms.sync.cancel")}
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TooltipProvider>
  );
};
